import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  message,
  Modal,
  Dropdown,
  Menu,
  Card,
  Form,
  Input,
  Row,
  Col,
  Button,
  Select,
  Pagination,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import {
  addModel,
  editModel,
  removeModel,
  fetchModelsByMake,
} from '../../../store/actions/vehicleModels-actions';

const { Option } = Select;

const VehicleModels = ({ models, makes }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedMakeList, setSelectedMakeList] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const { filteredModels } = useSelector((state) => state.models);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 30;

  const handleSubmitModel = () => {
    form
      .validateFields()
      .then((values) => {
        const { modelName } = values;

        if (editingModel) {
          dispatch(
            editModel({
              modelId: editingModel.id,
              body: { name: modelName },
            })
          );
        } else {
          dispatch(
            addModel({
              name: modelName,
              vehicleMakeId: selectedMake,
            })
          );
        }
        form.resetFields();
        setOpenModal(false);
        setEditingModel(null);
        setSelectedMake(null);
      })
      .catch((info) => {
        message.error('Model name and make are required!');
      });
  };

  const handleEditModel = (model) => {
    setEditingModel(model);
    form.setFieldsValue({
      modelName: model.name,
      vehicleMake: model.vehicleMakeId,
    });

    setSelectedMake(model.vehicleMakeId);
    setOpenModal(true);
  };

  const handleDeleteModel = (modelId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this model?',
      onOk: () => dispatch(removeModel(modelId)),
    });
  };

  const menuItems = (model) => [
    {
      label: t('common.edit'),
      key: 'edit',
      icon: <EditOutlined />,
      onClick: () => handleEditModel(model),
    },
    {
      label: t('common.delete'),
      key: 'delete',
      danger: true,
      icon: <DeleteOutlined />,
      onClick: () => handleDeleteModel(model.id),
    },
  ];

  useEffect(() => {
    if (selectedMakeList) {
      dispatch(fetchModelsByMake({ vehicleMakeId: selectedMakeList }));
    }
  }, [selectedMakeList, dispatch]);

  const displayedModels = selectedMakeList ? filteredModels : models;

  const filteredDisplayedModels = displayedModels.filter((model) =>
    model.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedModels = filteredDisplayedModels.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  let content;

  content = (
    <div className="container-fluid p-0">
      <Card
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <span>{t('vehicles.models')}</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <Select
                placeholder="Select Make"
                value={selectedMakeList}
                onChange={setSelectedMakeList}
                style={{ width: 200, marginRight: 16 }}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                allowClear
              >
                {makes.map((make) => (
                  <Option key={make.id} value={make.id}>
                    {make.name}
                  </Option>
                ))}
              </Select>
              <Input
                placeholder={t('common.searchByName')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 200, marginRight: 16 }}
              />
            </div>
            <Button
              type="primary"
              onClick={() => {
                setOpenModal(true);
                form.resetFields();
                setSelectedMake(null);
              }}
            >
              {t('common.add')} {t('vehicles.model')}
            </Button>
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap' }}>
          {paginatedModels.map((model) => (
            <Col key={model.id} xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Card style={{ flex: '1 0 auto' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      height: '100%',
                    }}
                  >
                    <div>
                      <h3>{model.name}</h3>
                    </div>
                    <Dropdown.Button
                      style={{ zIndex: 1 }}
                      overlay={<Menu items={menuItems(model)} />}
                    />
                  </div>
                </Card>
              </div>
            </Col>
          ))}
        </Row>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredDisplayedModels.length}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
          showQuickJumper
          style={{ marginTop: 16 }}
        />
      </Card>
    </div>
  );

  return (
    <>
      {content}
      <Modal
        title={
          editingModel
            ? t('common.edit') + ' ' + t('vehicles.model')
            : t('common.add') + ' ' + t('vehicles.model')
        }
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          setEditingModel(null);
          setSelectedMake(null);
        }}
        onOk={handleSubmitModel}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="modelName"
            label={t('common.name')}
            rules={[
              {
                required: true,
                message: t('form.validationMessages.requiredField', {
                  field: t('common.name'),
                }),
              },
            ]}
          >
            <Input placeholder="Enter model name" />
          </Form.Item>
          <Form.Item
            name="vehicleMake"
            label="Select Make"
            rules={[
              {
                required: true,
                message: t('form.validationMessages.requiredField', {
                  field: t('vehicles.make'),
                }),
              },
            ]}
          >
            <Select
              placeholder="Select a make"
              value={selectedMake}
              onChange={setSelectedMake}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              disabled={editingModel}
            >
              {makes.map((make) => (
                <Option key={make.id} value={make.id}>
                  {make.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default VehicleModels;
